import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { mediaUrl } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { useCurrency } from "@/context/CurrencyContext";

const GlassProducts = ({ products }) => {
  const { format } = useCurrency();
  if (!products?.length) return <p className="text-sm text-espresso/50">Pieces from this look will be added soon.</p>;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="look-products">
      {products.map((p) => (
        <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`look-product-${p.slug}`} className="group/card block">
          <div className="aspect-[3/4] overflow-hidden bg-sand mb-1.5 flex items-center justify-center">
            {p.image
              ? <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover/card:scale-[1.05]" />
              : <span className="font-serif text-espresso/45 text-[0.55rem] tracking-[0.12em] uppercase text-center px-1">Image soon</span>}
          </div>
          <p className="font-serif text-sm leading-tight group-hover/card:text-gold transition-colors">{p.name}</p>
          <p className="text-[0.72rem] text-espresso/60">{p.price != null ? format(p.price) : "On request"}</p>
        </Link>
      ))}
    </div>
  );
};

const LookbookDetail = () => {
  const { slug } = useParams();
  const [lb, setLb] = useState(undefined);
  useSEO(lb ? { title: lb.seo?.title || `${lb.title} | NoirbyAmara`, description: lb.seo?.description || lb.intro } : {});
  useEffect(() => { api.get(`/lookbooks/${slug}`).then((r) => setLb(r.data)).catch(() => setLb(null)); }, [slug]);

  if (lb === undefined) return <div className="min-h-[70vh] bg-ivory" />;
  if (lb === null) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 bg-ivory">
      <p className="font-serif text-4xl mb-4">This story isn't here.</p>
      <Link to="/lookbook" className="border border-espresso px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">Back to the Lookbook</Link>
    </div>
  );

  const looks = lb.looks || [];

  return (
    <div data-testid="lookbook-detail" className="bg-ivory text-espresso">
      <section className="relative h-[85vh] min-h-[480px] -mt-16 md:-mt-20 flex items-end overflow-hidden bg-cocoa">
        {lb.hero_film
          ? <video autoPlay muted loop playsInline preload="metadata" poster={lb.hero_image ? mediaUrl(lb.hero_image) : undefined} src={mediaUrl(lb.hero_film)} className="absolute inset-0 w-full h-full object-cover" />
          : lb.hero_image && <img src={mediaUrl(lb.hero_image)} alt={lb.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-cocoa/85 via-cocoa/20 to-cocoa/30" />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-[1600px] mx-auto w-full px-6 md:px-10 pb-16 md:pb-20 text-cream">
          <p className="overline text-gold mb-4">The Lookbook</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight">{lb.title}</h1>
          {lb.intro && <p className="mt-6 text-base md:text-lg text-cream/75 max-w-xl leading-relaxed">{lb.intro}</p>}
        </motion.div>
      </section>

      <div className="space-y-24 md:space-y-40 py-24 md:py-36">
        {looks.length === 0 && (
          <p className="text-center font-serif text-3xl text-espresso/50">Looks are being styled — return shortly.</p>
        )}
        {looks.map((l, i) => (
          <motion.section key={l.id || i} data-testid={`look-${i}`}
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 1 }}
            className="relative">
            <div className="relative w-full h-[75vh] md:h-[92vh] overflow-hidden bg-cocoa">
              {l.image
                ? <img src={mediaUrl(l.image)} alt={l.caption || `${lb.title} look ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                : <span className="absolute inset-0 flex items-center justify-center font-serif text-cream/40 text-lg tracking-[0.2em] uppercase">Imagery arriving soon</span>}
              <div className="absolute inset-0 bg-gradient-to-t from-cocoa/50 to-transparent md:bg-none" />
            </div>
            <div className={`relative z-10 mx-auto md:mx-0 w-[92%] md:w-auto md:max-w-sm -mt-24 md:mt-0 md:absolute md:bottom-10 ${i % 2 ? "md:right-10" : "md:left-10"}`}>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }}
                className="bg-ivory/80 backdrop-blur-2xl border border-cream/50 p-6 md:p-7 shadow-[0_20px_60px_-30px_rgba(43,24,18,0.5)]" data-testid="look-panel">
                <p className="overline text-gold mb-3">Look {String(i + 1).padStart(2, "0")}</p>
                {l.caption && <p className="font-serif text-xl md:text-2xl leading-snug mb-5">{l.caption}</p>}
                <GlassProducts products={l.products} />
              </motion.div>
            </div>
          </motion.section>
        ))}
      </div>

      <div className="pb-28 text-center">
        <Link to="/lookbook" data-testid="lookbook-back" className="inline-block border border-espresso px-9 py-4 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">
          All stories
        </Link>
      </div>
    </div>
  );
};

export default LookbookDetail;
